import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Button,
  Modal,
  Input,
  message,
  Upload,
  Descriptions,
  Tooltip,
  Card,
  Space,
  Typography,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  addReportToPatient,
  cancel_order,
  edit_order,
  fetchReport,
  fetchTransactions,
  getOrders,
  regenerateInvoice,
} from "../../endpoints/API";
import { useSelector } from "react-redux";
import CreateOrder from "./CreateOrder";

const { Text } = Typography;

function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
  };
}

// Status → color mapping
const STATUS_COLORS = {
  pending:             { bg: "#FAEEDA", color: "#633806", border: "#FAC775" },
  accepted:            { bg: "#E6F1FB", color: "#0C447C", border: "#B5D4F4" },
  reached:             { bg: "#EEEDFE", color: "#3C3489", border: "#CECBF6" },
  collection_started:  { bg: "#E1F5EE", color: "#085041", border: "#9FE1CB" },
  collected:           { bg: "#E1F5EE", color: "#085041", border: "#9FE1CB" },
  handover:            { bg: "#EEEDFE", color: "#3C3489", border: "#CECBF6" },
  accession:           { bg: "#E6F1FB", color: "#0C447C", border: "#B5D4F4" },
  completed:           { bg: "#EAF3DE", color: "#27500A", border: "#C0DD97" },
  partial_report:      { bg: "#FAEEDA", color: "#633806", border: "#FAC775" },
  cancelled:           { bg: "#FCEBEB", color: "#791F1F", border: "#F7C1C1" },
  rejected:            { bg: "#FCEBEB", color: "#791F1F", border: "#F7C1C1" },
  on_hold:             { bg: "#F1EFE8", color: "#444441", border: "#D3D1C7" },
};

export default function OrderList() {
  const [dataSourceOrder, setDataSourceOrder] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewOrderItemsModalVisible, setViewOrderItemsModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const [cancelOrderModalVisible, setIsCancelOrderModalVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [globalSearch, setGlobalSearch] = useState("");
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [editOrderData, setEditOrderData] = useState(null);

  const client = useSelector((state) => state.user.user);
  const [statusFilter, setStatusFilter] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [regeneratingId, setRegeneratingId] = useState(null);
  const { isMobile, isTablet } = useBreakpoint();

  const formatDateTime = (datetime) => {
    const dateObj = new Date(datetime);
    const formattedDate = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = dateObj.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { formattedDate, formattedTime };
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async (page = 1, pageSize = 10, search = "", status = "") => {
    try {
      setLoading(true);
      const res = await getOrders(page, pageSize, search.trim(), status);
      setDataSourceOrder(res.data.results);
      setPagination({ current: page, pageSize, total: res.data.count });
    } catch (error) {
      message.error(error?.response?.data?.error || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderItems = (id, orderNumber, orderItems) => {
    setDataSource(orderItems || []);
    setViewOrderItemsModalVisible(true);
  };

  const closeOrderItemsModal = () => {
    setViewOrderItemsModalVisible(false);
    setDataSource([]);
  };

  const handleViewInvoice = async (transactions) => {
    try {
      const res = await fetchTransactions(
        `secured_media_access/media/${transactions[0]?.invoice_pdf}/`
      );
      if (!res.statusText === "OK") throw new Error("Failed to fetch invoice");
      const blob = res?.data;
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (error) {
      message.error(error?.response?.data?.error || "Could not open the invoice.");
    }
  };

  const handleRegenerateInvoice = async (orderId) => {
    try {
      setRegeneratingId(orderId);
      await regenerateInvoice(orderId);
      message.success("Invoice regenerated successfully");
      fetchOrders(pagination.current, pagination.pageSize, globalSearch, statusFilter);
    } catch (error) {
      message.error(error?.response?.data?.error || "Failed to regenerate invoice");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleReportUpload = async (file, patientId, orderId) => {
    try {
      const formData = new FormData();
      formData.append("report", file);
      formData.append("order_id", orderId);
      formData.append("order_patient_id", patientId);
      formData.append("order_id_patient_id", `${orderId}-${patientId}`);
      const response = await addReportToPatient(formData);
      if (response.statusText !== "OK") throw new Error("Failed to upload report");
      message.success("Report uploaded successfully!");
      fetchOrders(pagination.current, pagination.pageSize, globalSearch, statusFilter);
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
      throw err;
    }
  };

  const handleViewReport = async (reportUrl) => {
    try {
      const res = await fetchReport(
        `secured_media_access/${reportUrl.replace(/^\/+/, "")}/`
      );
      if (res.statusText !== "OK") throw new Error("Failed to fetch report");
      const blob = res?.data;
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      message.error("Please provide a cancellation reason");
      return;
    }
    try {
      const payload = {
        reason: cancellationReason,
        user: "admin",
        status: "cancelled",
        userID: client.id,
      };
      await cancel_order(selectedOrderId, payload);
      message.success("Order cancelled successfully");
      fetchOrders(pagination.current, pagination.pageSize, globalSearch, statusFilter);
    } catch (error) {
      message.error(error?.response?.data?.error || "Failed to cancel order");
    } finally {
      setIsCancelOrderModalVisible(false);
      setCancellationReason("");
    }
  };

  const handleViewDetails = (record) => {
    setSelectedItem(record);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedItem(null);
  };

  // ── Redesigned Mobile Card ──────────────────────────────────────────────────
  const MobileOrderCard = ({ record }) => {
    const { formattedDate, formattedTime } = formatDateTime(record.place_datetime);
    const scheduledDT = record.schedule_datetime || record.place_datetime;
    const { formattedDate: sDate, formattedTime: sTime } = formatDateTime(scheduledDT);
    const isCancelled = !!record.cancelled_date_time || !!record.cancellation_reason;
    const isCancellable = ["pending", "accepted", "reached"].includes(record.order_status);
    const statusStyle = STATUS_COLORS[record.order_status] || STATUS_COLORS.on_hold;

    // Shared label style
    const label = {
      fontSize: 10,
      fontWeight: 500,
      color: "var(--color-text-tertiary, #999)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 2,
    };
    const value = {
      fontSize: 13,
      color: "var(--color-text-primary, #111)",
    };
    const valueMuted = {
      fontSize: 13,
      color: "var(--color-text-secondary, #666)",
    };

    return (
      <div
        style={{
          background: "var(--color-background-primary, #fff)",
          border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.12))",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* ── Card Header: order number + status badge ── */}
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontSize: 11, ...label, marginBottom: 1 }}>Order</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
              #{record.order_number}
            </div>
          </div>
          {record.order_status && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 10px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 500,
                background: statusStyle.bg,
                color: statusStyle.color,
                border: `0.5px solid ${statusStyle.border}`,
                textTransform: "capitalize",
                whiteSpace: "nowrap",
              }}
            >
              {record.order_status.replace(/_/g, " ")}
            </span>
          )}
        </div>

        {/* ── Divider ── */}
        <div style={{ height: "0.5px", background: "var(--color-border-tertiary, rgba(0,0,0,0.08))", margin: "0 16px" }} />

        {/* ── Customer & Contact info box ── */}
        <div
          style={{
            margin: "12px 16px",
            background: "var(--color-background-secondary, #f9f9f7)",
            border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
            borderRadius: 8,
            padding: "10px 12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 16px",
          }}
        >
          <div>
            <div style={label}>Customer</div>
            <div style={value}>{record.customer || "—"}</div>
          </div>
          <div>
            <div style={label}>Contact</div>
            <div style={value}>{record.point_of_contacts || "—"}</div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>Address</div>
            <Tooltip title={record.address} trigger="click">
              <div
                style={{
                  ...valueMuted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                }}
              >
                {record.address || "—"}
              </div>
            </Tooltip>
          </div>
        </div>

        {/* ── Dates + OTP row ── */}
        <div
          style={{
            margin: "0 16px 12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 16px",
          }}
        >
          <div>
            <div style={label}>Placed</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span
                style={{
                  fontSize: 12,
                  background: "var(--color-background-secondary, #f5f5f3)",
                  color: "var(--color-text-secondary)",
                  border: "0.5px solid var(--color-border-tertiary)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  display: "inline-block",
                  width: "fit-content",
                }}
              >
                {formattedDate}
              </span>
              <span
                style={{
                  fontSize: 12,
                  background: "var(--color-background-secondary, #f5f5f3)",
                  color: "var(--color-text-secondary)",
                  border: "0.5px solid var(--color-border-tertiary)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  display: "inline-block",
                  width: "fit-content",
                }}
              >
                {formattedTime}
              </span>
            </div>
          </div>

          <div>
            <div style={label}>Scheduled</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span
                style={{
                  fontSize: 12,
                  background: "var(--color-background-secondary, #f5f5f3)",
                  color: "var(--color-text-secondary)",
                  border: "0.5px solid var(--color-border-tertiary)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  display: "inline-block",
                  width: "fit-content",
                }}
              >
                {sDate}
              </span>
              <span
                style={{
                  fontSize: 12,
                  background: "var(--color-background-secondary, #f5f5f3)",
                  color: "var(--color-text-secondary)",
                  border: "0.5px solid var(--color-border-tertiary)",
                  borderRadius: 5,
                  padding: "2px 7px",
                  display: "inline-block",
                  width: "fit-content",
                }}
              >
                {sTime}
              </span>
            </div>
          </div>

          {record.otp && (
            <div>
              <div style={label}>OTP</div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  background: "#E6F1FB",
                  color: "#0C447C",
                  border: "0.5px solid #B5D4F4",
                  borderRadius: 5,
                  padding: "2px 10px",
                  display: "inline-block",
                  letterSpacing: "0.08em",
                }}
              >
                {record.otp}
              </span>
            </div>
          )}
        </div>

        {/* ── Cancellation info box (only if cancelled) ── */}
        {(record.cancelled_date_time || record.cancellation_reason) && (
          <div
            style={{
              margin: "0 16px 12px",
              background: "#FCEBEB",
              border: "0.5px solid #F7C1C1",
              borderRadius: 8,
              padding: "10px 12px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 16px",
            }}
          >
            {record.cancelled_date_time && (
              <div>
                <div style={{ ...label, color: "#A32D2D" }}>Cancelled at</div>
                <div style={{ fontSize: 12, color: "#791F1F" }}>
                  {formatDateTime(record.cancelled_date_time).formattedDate}{" "}
                  {formatDateTime(record.cancelled_date_time).formattedTime}
                </div>
              </div>
            )}
            {record.cancelled_by && (
              <div>
                <div style={{ ...label, color: "#A32D2D" }}>Cancelled by</div>
                <div style={{ fontSize: 12, color: "#791F1F" }}>{record.cancelled_by}</div>
              </div>
            )}
            {record.cancellation_reason && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ ...label, color: "#A32D2D" }}>Reason</div>
                <div style={{ fontSize: 12, color: "#791F1F" }}>{record.cancellation_reason}</div>
              </div>
            )}
          </div>
        )}

        {/* ── Action footer ── */}
        <div
          style={{
            borderTop: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
            padding: "10px 16px",
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
          }}
        >
          {/* Order items */}
          <Button
            size="small"
            onClick={() =>
              handleViewOrderItems(record?.order_id, record?.order_number, record.order_items)
            }
          >
            Items
          </Button>

          {/* Invoice */}
          {record.transactions?.length > 0 ? (
            <>
              <Button
                size="small"
                style={{ background: "#E6F1FB", color: "#0C447C", borderColor: "#B5D4F4" }}
                onClick={() => handleViewInvoice(record.transactions)}
              >
                Invoice
              </Button>
              <Button
                size="small"
                loading={regeneratingId === record.id}
                onClick={() => handleRegenerateInvoice(record.id)}
              >
                Regen.
              </Button>
            </>
          ) : (
            <span
              style={{
                fontSize: 11,
                padding: "2px 8px",
                borderRadius: 5,
                background: "#F1EFE8",
                color: "#5F5E5A",
                border: "0.5px solid #D3D1C7",
              }}
            >
              Not paid
            </span>
          )}

          {/* Spacer to push edit + cancel to right */}
          <div style={{ flex: 1 }} />

          {/* Edit */}
          <Button
            size="small"
            type="text"
            disabled={isCancelled}
            onClick={async () => {
              if (!isCancelled) {
                const res = await edit_order(record.id);
                setEditOrderData(res.data.order);
                setShowCreateOrder(true);
              }
            }}
            icon={<EditOutlined />}
          />

          {/* Cancel */}
          {record.cancellation_reason === null && isCancellable && (
            <Button
              size="small"
              danger
              onClick={() => {
                setSelectedOrderId(record.id);
                setIsCancelOrderModalVisible(true);
              }}
            >
              Cancel
            </Button>
          )}
          {record.cancellation_reason === null && !isCancellable && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Cannot cancel
            </Text>
          )}
        </div>
      </div>
    );
  };
  // ───────────────────────────────────────────────────────────────────────────

  // ── Desktop/Tablet columns (unchanged) ──
  const columns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      fixed: "left",
      width: 130,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      width: 130,
      render: (status) => (status ? <Tag>{status}</Tag> : null),
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Accepted", value: "accepted" },
        { text: "Reached", value: "reached" },
        { text: "Collection Started", value: "collection_started" },
        { text: "Collected", value: "collected" },
        { text: "Handover", value: "handover" },
        { text: "Accession", value: "accession" },
        { text: "Completed", value: "completed" },
        { text: "Partial Report", value: "partial_report" },
        { text: "Cancelled", value: "cancelled" },
        { text: "Rejected", value: "rejected" },
        { text: "On Hold", value: "on_hold" },
      ],
      filterMultiple: false,
    },
    {
      title: "Place Date-Time",
      dataIndex: "place_datetime",
      key: "place_datetime",
      width: 155,
      render: (placeDateTime) => {
        const { formattedDate, formattedTime } = formatDateTime(placeDateTime);
        return (
          <div>
            <Tag>{formattedDate}</Tag>
            <Tag>{formattedTime}</Tag>
          </div>
        );
      },
    },
    {
      title: "Schedule Date-Time",
      dataIndex: "schedule_datetime",
      key: "schedule_datetime",
      width: 155,
      render: (scheduleDateTime, record) => {
        const datetimeToFormat = scheduleDateTime || record.place_datetime;
        const { formattedDate, formattedTime } = formatDateTime(datetimeToFormat);
        return (
          <div>
            <Tag>{formattedDate}</Tag>
            <Tag>{formattedTime}</Tag>
          </div>
        );
      },
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 180,
      render: (address) => (
        <Tooltip title={address} trigger="click">
          <div
            style={{
              maxWidth: 160,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer",
            }}
          >
            {address}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "OTP",
      dataIndex: "otp",
      key: "otp",
      width: 80,
      render: (otp) => (
        <span>{otp ? <Tag color="blue">{otp}</Tag> : <Tag color="blue">-</Tag>}</span>
      ),
    },
    {
      title: "Point of Contact",
      dataIndex: "point_of_contacts",
      key: "point_of_contacts",
      width: 140,
    },
    {
      title: "Order Item",
      key: "order_item",
      width: 130,
      render: (_, record) => (
        <Button
          size="small"
          onClick={() =>
            handleViewOrderItems(record?.order_id, record?.order_number, record.order_items)
          }
        >
          View Order Item
        </Button>
      ),
    },
    {
      title: "Invoice",
      key: "transactions",
      dataIndex: "transactions",
      width: 180,
      render: (transactions, record) =>
        transactions?.length !== 0 ? (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Button size="small" type="primary" ghost onClick={() => handleViewInvoice(transactions)}>
              View
            </Button>
            <Button
              size="small"
              loading={regeneratingId === record.id}
              onClick={() => handleRegenerateInvoice(record.id)}
            >
              Regenerate
            </Button>
          </div>
        ) : (
          <Tag>Not Paid</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (_, record) => {
        const isCancelled = !!record.cancelled_date_time || !!record.cancellation_reason;
        return (
          <Button
            type="link"
            disabled={isCancelled}
            onClick={async () => {
              if (!isCancelled) {
                const res = await edit_order(record.id);
                setEditOrderData(res.data.order);
                setShowCreateOrder(true);
              }
            }}
          >
            <EditOutlined />
          </Button>
        );
      },
    },
    {
      title: "Cancelled Date Time",
      dataIndex: "cancelled_date_time",
      key: "cancelled_date_time",
      width: 155,
      render: (cancelled_date_time) => {
        if (cancelled_date_time) {
          const { formattedDate, formattedTime } = formatDateTime(cancelled_date_time);
          return (
            <div>
              <Tag>{formattedDate}</Tag>
              <Tag>{formattedTime}</Tag>
            </div>
          );
        }
        return <span>-</span>;
      },
    },
    {
      title: "Cancelled By",
      dataIndex: "cancelled_by",
      key: "cancelled_by",
      width: 110,
    },
    {
      title: "Cancel Order/Reason",
      key: "cancel",
      width: 160,
      render: (_, record) => {
        const { cancellation_reason, order_status, id } = record;
        const isCancellable = ["pending", "accepted", "reached"].includes(order_status);
        if (cancellation_reason === null) {
          return isCancellable ? (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setSelectedOrderId(id);
                setIsCancelOrderModalVisible(true);
              }}
            >
              Cancel
            </Button>
          ) : (
            "Cannot be cancelled"
          );
        } else {
          return <span className="truncate w-[.5rem]">{cancellation_reason}</span>;
        }
      },
    },
  ];

  const orderItemColumns = [
    {
      title: "Patient ID",
      dataIndex: "patientid",
      key: "patientid",
      render: (_, record) => {
        const orderNumber = record.order_number || "-";
        const patientId = record.family_member_id || "-";
        return `${orderNumber}-${patientId}`;
      },
    },
    {
      title: "Test Name",
      dataIndex: "test_name",
      key: "test_name",
    },
    {
      title: "Family Member",
      dataIndex: "family_member_name",
      key: "family_member_name",
    },
    {
      title: "Package Name",
      dataIndex: "package_name",
      key: "package_name",
    },
    {
      title: "Reports",
      dataIndex: "reports",
      key: "reports",
      render: (reports, record) => {
        const patientId = `${record.family_member_id}`;
        const orderId = record.order_number;
        const patientReportKey = `${orderId}-${patientId}`;
        const patientReport = reports?.find(
          (r) => r.order_familymember_id === patientReportKey
        );
        if (patientReport?.report_url) {
          return (
            <Button size="small" onClick={() => handleViewReport(patientReport.report_url)}>
              View
            </Button>
          );
        }
        return (
          <Upload
            accept=".pdf"
            showUploadList={false}
            customRequest={({ file, onSuccess, onError }) => {
              handleReportUpload(file, patientId, orderId)
                .then(() => onSuccess())
                .catch((err) => onError(err));
            }}
            beforeUpload={(file) => {
              const isPDF = file.type === "application/pdf";
              if (!isPDF) {
                message.error("Only PDF files are allowed!");
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Upload
            </Button>
          </Upload>
        );
      },
    },
    {
      title: "Details",
      key: "details",
      render: (_, record) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
      ),
    },
  ];

  return (
    <>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: isMobile ? 15 : 18 }}>
          Total Orders: {pagination?.total}
        </h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditOrderData(null);
            setShowCreateOrder(true);
          }}
        >
          Create Order
        </Button>
      </div>

      {/* ── Search ── */}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by order number, name, or date"
          enterButton
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onSearch={() => fetchOrders(1, pagination.pageSize, globalSearch, statusFilter)}
          allowClear
          style={{ width: "100%" }}
        />
      </div>

      {/* ── Mobile cards / Desktop table ── */}
      {isMobile ? (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 32 }}>Loading...</div>
          ) : dataSourceOrder.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#999" }}>
              No orders found.
            </div>
          ) : (
            dataSourceOrder.map((record) => (
              <MobileOrderCard key={record.id} record={record} />
            ))
          )}
          {/* Mobile pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 12,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Button
              size="small"
              disabled={pagination.current === 1}
              onClick={() =>
                fetchOrders(pagination.current - 1, pagination.pageSize, globalSearch, statusFilter)
              }
            >
              Prev
            </Button>
            <span style={{ lineHeight: "24px", fontSize: 13 }}>
              Page {pagination.current} /{" "}
              {Math.ceil(pagination.total / pagination.pageSize) || 1}
            </span>
            <Button
              size="small"
              disabled={
                pagination.current >= Math.ceil(pagination.total / pagination.pageSize)
              }
              onClick={() =>
                fetchOrders(pagination.current + 1, pagination.pageSize, globalSearch, statusFilter)
              }
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            loading={loading}
            rowKey="id"
            dataSource={dataSourceOrder}
            columns={columns}
            scroll={{ x: 1400 }}
            size={isTablet ? "small" : "middle"}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              responsive: true,
            }}
            onChange={(paginationInfo, filters) => {
              const status = filters.order_status?.[0] || "";
              setStatusFilter(status);
              fetchOrders(
                paginationInfo.current,
                paginationInfo.pageSize,
                globalSearch,
                status
              );
            }}
          />
        </div>
      )}

      {/* ── Order Items Modal ── */}
      <Modal
        title="Order Items"
        open={viewOrderItemsModalVisible}
        onCancel={closeOrderItemsModal}
        footer={false}
        width={isMobile ? "98vw" : isTablet ? "90vw" : 800}
        style={isMobile ? { top: 8 } : {}}
        centered={isMobile}
      >
        <div style={{ overflowX: "auto" }}>
          <Table
            columns={orderItemColumns}
            dataSource={dataSource}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            size={isMobile ? "small" : "middle"}
            scroll={{ x: isMobile ? 500 : undefined }}
            locale={{ emptyText: "No order items available for this order." }}
          />
        </div>
      </Modal>

      {/* ── Create / Edit Order Modal ── */}
      <Modal
        title={editOrderData ? "Edit Order" : "Create Order"}
        open={showCreateOrder}
        onCancel={() => {
          setShowCreateOrder(false);
          setEditOrderData(null);
        }}
        footer={null}
        width={isMobile ? "98vw" : isTablet ? "90vw" : 900}
        style={isMobile ? { top: 8 } : {}}
        centered={isMobile}
        destroyOnClose
      >
        <CreateOrder
          initialData={editOrderData}
          onSuccess={() => {
            setShowCreateOrder(false);
            fetchOrders(pagination.current, pagination.pageSize, globalSearch, statusFilter);
          }}
        />
      </Modal>

      {/* ── Cancel Order Modal ── */}
      <Modal
        title="Cancel Order"
        open={cancelOrderModalVisible}
        onOk={handleCancelOrder}
        onCancel={() => {
          setIsCancelOrderModalVisible(false);
          setCancellationReason("");
        }}
        okText="Submit"
        cancelText="Close"
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <p>Please provide a reason for cancelling this order:</p>
        <Input.TextArea
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          rows={4}
        />
      </Modal>

      {/* ── Order Item Details Modal ── */}
      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        title="Order Item Details"
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        {selectedItem && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Wellness Executive">
              {selectedItem.we_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Accepted At">
              {selectedItem.we_accepted_at
                ? `${formatDateTime(selectedItem.we_accepted_at).formattedDate} ${
                    formatDateTime(selectedItem.we_accepted_at).formattedTime
                  }`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Handover At">
              {selectedItem.we_handover_at
                ? `${formatDateTime(selectedItem.we_handover_at).formattedDate} ${
                    formatDateTime(selectedItem.we_handover_at).formattedTime
                  }`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Closed At">
              {selectedItem.we_closed_at
                ? `${formatDateTime(selectedItem.we_closed_at).formattedDate} ${
                    formatDateTime(selectedItem.we_closed_at).formattedTime
                  }`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Pouch Barcode">
              {selectedItem.pouch_barcode || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Bill ID">
              {selectedItem.bill_id || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
}